// MagicDeskView — personalised lesson regeneration.
// UX contract (per the spec):
//   • Setup asks ONLY for the student's interest (auto-detected OR typed).
//     No "what to focus on", no length picker, no extras.
//   • The regenerated lesson renders through the SAME <LessonView />
//     component as the teacher's original lesson — identical chrome,
//     identical typography, identical section cards. The teacher's
//     facts/headings stay; only the examples + applications are retuned
//     to the student's world.
//   • The chat lives in the right Tutor pane (LearnNowPage), not here.
//     Setting an active MagicDesk generation registers its id with the
//     parent so the tutor pane can route doubts to the MagicDesk-grounded
//     chat seamlessly. No second chat window.
import React, { useEffect, useState } from 'react';
import { Sparkles, Wand2, Bookmark, AlertTriangle } from 'lucide-react';
import { InlineSparkle } from '../../../../components/Loader';
import { featureFill, featureInk } from './featureColors';
import GenerationsShell from './GenerationsShell';
import AutoTunedHeader from './AutoTunedHeader';
import TopicChipPicker from './TopicChipPicker';
import LessonView from './LessonView';

const TINT = '#8B5CF6';

export default function MagicDeskView({ lessonId, lessonTitle, onActiveGenChange }) {
  return (
    <GenerationsShell
      lessonId={lessonId}
      kind="magicdesk"
      kindLabel="MagicDesk"
      tint={TINT}
      defaultCost={5}
      showCreditsChip={false}
      emptyHint="Regenerate this lesson tuned to your interests."
      setupPanel={({ onGenerate, generating }) => (
        <Setup lessonId={lessonId} lessonTitle={lessonTitle}
          onGenerate={(payload) => onGenerate(`/student/lesson/${lessonId}/magicdesk/generate`, payload)}
          generating={generating} />
      )}
      renderBody={(gen, ctx) => (
        <Body gen={gen} lessonTitle={lessonTitle} onActiveGenChange={onActiveGenChange} ctx={ctx} />
      )}
    />
  );
}

// ─── Setup — ONLY interest (auto OR typed). No focus, no length picker. ────
function Setup({ lessonId, lessonTitle, onGenerate, generating }) {
  const [autotuned, setAutotuned] = useState(true);
  const [topic, setTopic] = useState(null);
  // Mirror the TopicChipPicker's custom-text box so the Retune button knows
  // a topic has effectively been typed even if the student hasn't clicked
  // "Use this". Without this, clicking Retune right after typing leaves the
  // CTA disabled and the page looks frozen — that's the "not generating
  // when custom topic given" bug.
  const [pendingCustomText, setPendingCustomText] = useState('');

  // Effective interest: a confirmed topic takes precedence; otherwise we
  // promote whatever the student has typed into the custom box.
  const effectiveInterest = topic?.label || pendingCustomText.trim();
  const canGenerate = autotuned || !!effectiveInterest;

  return (
    <div data-testid="magicdesk-setup-panel">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4" style={{ color: featureInk(TINT) }} />
        <h2 className="text-base font-semibold" style={{ fontFamily: 'var(--st-font-heading)' }}>
          Retune &ldquo;{lessonTitle}&rdquo; to your world
        </h2>
      </div>
      <p className="text-xs mb-4" style={{ color: 'var(--st-mute)' }}>
        Same lesson your teacher published — examples and applications rewritten
        in the topic you care about.
      </p>

      <AutoTunedHeader lessonId={lessonId} kind="magicdesk" tint={TINT}
        autotuned={autotuned} onChange={setAutotuned}
        testIdPrefix="magicdesk-autotune" />

      {!autotuned && (
        <div className="mt-1">
          <TopicChipPicker lessonId={lessonId} kind="magicdesk" tint={TINT}
            value={topic} onChange={setTopic}
            onPendingCustomTextChange={setPendingCustomText}
            testIdPrefix="magicdesk-topics" />
        </div>
      )}

      <div className="mt-5 pt-4 border-t flex items-center justify-between gap-3" style={{ borderColor: 'var(--st-border)' }}>
        <div className="text-xs" style={{ color: 'var(--st-mute)' }}>
          Cost scales with lesson length (~5 Super Credits typical). Refunded on failure.
        </div>
        <button onClick={() => onGenerate({
            length: 'medium',                      // sensible default — no UI control
            autotuned,
            interest_focus: !autotuned ? (effectiveInterest || undefined) : undefined,
          })}
          disabled={generating || !canGenerate}
          data-testid="magicdesk-generate"
          className="rounded-full px-5 py-2 text-sm font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-50 transition-transform "
          style={{ background: TINT }}>
          {generating ? <InlineSparkle label="Retuning lesson" /> : <Wand2 className="w-3.5 h-3.5" />} {generating ? 'Retuning…' : 'Retune lesson'}
        </button>
      </div>
    </div>
  );
}

// ─── Body — render via <LessonView /> so the UX matches 1:1 ───────────────
const MAGICDESK_PHASES = [
  { id: 'queued',       label: 'Queued',       sub: 'Reserving a spot in the studio' },
  { id: 'transforming', label: 'Personalising', sub: 'Rewriting examples to match your interests' },
  { id: 'ready',        label: 'Finishing up',  sub: 'Putting the lesson back together' },
];

function Generating({ gen }) {
  const prog = gen.progress || {};
  const phase = prog.phase || 'queued';
  const idx = Math.max(0, MAGICDESK_PHASES.findIndex(p => p.id === phase));
  const current = MAGICDESK_PHASES[idx] || MAGICDESK_PHASES[0];
  const total = prog.total_sections || 0;
  const done = prog.sections_done || 0;
  const pct = phase === 'queued' ? 5
    : phase === 'transforming' ? 15 + (total ? Math.round((done / total) * 75) : 0)
    : 100;

  return (
    <div data-testid="magicdesk-generating" className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: TINT, background: 'var(--st-card)' }}>
      <div className="p-6 sm:p-8 text-center" style={{ background: `linear-gradient(135deg, ${featureFill(TINT)}, color-mix(in srgb, ${TINT} 45%, #000))` }}>
        <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur mx-auto mb-3 flex items-center justify-center">
          <InlineSparkle size={40} label="Personalising lesson" />
        </div>
        <div className="text-white text-[15px] font-semibold">{current.label}…</div>
        <div className="text-white/80 text-xs mt-1">{current.sub}</div>
      </div>
      <div className="p-4">
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--st-border)' }}>
          <div className="h-full rounded-full transition" style={{ width: `${pct}%`, background: TINT }} />
        </div>
        <div className="text-xs mt-3 text-center" style={{ color: 'var(--st-mute)' }}>
          Personalising this lesson — this keeps cooking even if you close this
          tab, so feel free to come back later.
        </div>
      </div>
    </div>
  );
}

function FailedCard({ gen, onReset }) {
  const reason = gen?.progress?.error || 'Something went wrong while personalising this lesson.';
  return (
    <div data-testid="magicdesk-failed" className="rounded-2xl border-2 p-8 text-center" style={{ borderColor: '#DC2626', background: 'var(--st-card)' }}>
      <AlertTriangle className="w-8 h-8 mx-auto mb-3" style={{ color: '#DC2626' }} />
      <div className="text-sm font-semibold" style={{ color: 'var(--st-ink)' }}>Personalisation failed</div>
      <div className="text-xs mt-1.5" style={{ color: 'var(--st-mute)' }}>{reason}</div>
      <button onClick={onReset} data-testid="magicdesk-failed-reset"
        className="mt-4 rounded-xl px-4 py-2 text-xs font-semibold text-white" style={{ background: featureFill(TINT) }}>
        Back to Setup
      </button>
    </div>
  );
}

function Body({ gen, lessonTitle, onActiveGenChange, ctx }) {
  // Tell LearnNowPage which MagicDesk gen is active so the right Tutor
  // pane can route chat to /student/generations/{gen.id}/magicdesk/chat.
  // Kept as this component's ONLY hook, called unconditionally before the
  // status branches below — gen.status transitions generating -> ready on
  // the SAME mounted instance via GenerationsShell's poll, so this hook's
  // count must stay invariant across that transition (a conditional
  // early-return placed BEFORE a hook would violate Rules of Hooks).
  useEffect(() => {
    if (typeof onActiveGenChange === 'function') onActiveGenChange(gen.id);
    return () => { if (typeof onActiveGenChange === 'function') onActiveGenChange(null); };
  }, [gen.id, onActiveGenChange]);

  if (gen.status === 'generating') return <Generating gen={gen} />;
  if (gen.status === 'failed') return <FailedCard gen={gen} onReset={() => ctx?.setActiveGen?.(null)} />;

  const c = gen.content || {};

  // Adapt the generation doc into the same `lesson` shape <LessonView />
  // already knows how to render. The MagicDesk Transformer preserves the
  // teacher's exact section structure / order / titles / tables / images,
  // so we pass `section` through verbatim — every field LessonView reads
  // (title, content, images, tables, sub-blocks) survives the round-trip.
  const lessonLike = {
    id: gen.id,
    chapter_label: c.source_chapter_label || 'MagicDesk · Personalised',
    published_at: gen.created_at,
    title: gen.title || lessonTitle,
    teacher_name: c.source_teacher_name,
    summary: c.summary || gen.preview || '',
    key_topics: c.source_key_topics || [],
    references: c.source_references || [],
    framework_sections: (c.sections || []).map((s, i) => ({
      ...s,
      id: s.id || `mds-${i}`,
      title: s.title || s.heading || `Section ${i + 1}`,
      content: s.content ?? s.body ?? '',
    })),
  };

  return (
    <div data-testid="magicdesk-body">
      <LessonView lesson={lessonLike} />
    </div>
  );
}

// ─── Re-exported Hero — used by SuperNarrateView for its own gradient
//   header card. Kept here so other "generation" surfaces (Super Narrate,
//   etc.) can share a single visual.                                       ─
export function Hero({ gen, tint, icon }) {
  return (
    <div className="rounded-2xl p-5 text-white relative overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${tint}, color-mix(in srgb, ${tint} 60%, #000))` }}>
      <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
      <div className="relative flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">{icon}</div>
        <div className="min-w-0">
          <div className="text-2xl font-semibold leading-tight" style={{ fontFamily: 'var(--st-font-heading)' }}>{gen.title}</div>
          {gen.preview && <div className="text-xs opacity-90 mt-1 line-clamp-2">{gen.preview}</div>}
          <div className="text-xs font-semibold opacity-80 mt-1.5 inline-flex items-center gap-1">
            <Bookmark className="w-3 h-3" /> Saved · {gen.created_at ? new Date(gen.created_at).toLocaleString(undefined, { hour12: true }) : ''}
          </div>
        </div>
      </div>
    </div>
  );
}
